import { fetchGet } from "../..";
import { Channel } from "../../../components/Screens/Watch/Live/LiveStreamSection";
import { APIResponse } from "../../../types/responses/APIResponse";
import { MemberPlans } from "../../../types/memberPlans";
import {
  AlternateSlot,
  AlternateSlots,
  ScheduleSlot,
  ScheduleSlotItem,
} from "../../../types/tvSchedule";
import { Player } from "../../../types/replay";
import { Stream } from "../../../types/stream";

const timeOptions = {
  hour: "numeric",
  minute: "numeric",
} as const;

const transformSlots = (
  slots: ScheduleSlot[] | AlternateSlot[],
  type: "main-channel" | "alternate-channel"
): ScheduleSlotItem[] => {
  return slots
    ?.map((slot) => {
      const { datetime, episode, id } = slot;
      return {
        title: episode.programme.title,
        description: episode.epg_synopsis,
        startAt: datetime.start,
        id,
        type,
      } as ScheduleSlotItem;
    })
    .filter(Boolean);
  // .flat();
};

export const getUpNext = async (
  includeRTVPlay = true
): Promise<ScheduleSlotItem[]> => {
  const { schedule_slots, alternate_slots } = (await fetchGet(
    "videos/streams"
  )) as {
    schedule_slots: ScheduleSlot[];
    alternate_slots: AlternateSlots;
  };

  const mainChannelPrograms = transformSlots(schedule_slots, "main-channel");

  const alternateChannelPrograms =
    transformSlots(
      Object.values(alternate_slots || {})[0],
      "alternate-channel"
    ) ?? [];

  if (includeRTVPlay)
    return [...mainChannelPrograms, ...alternateChannelPrograms];
  return [...mainChannelPrograms];
};

/**
 * Fetches and processes live stream data to generate a list of channel information.
 *
 * This function retrieves stream and schedule slot data from the `videos/streams` endpoint,
 * filters for active streams, and categorizes them into different types of channels:
 * - Main live channel (e.g., Racing TV)
 * - Racing TV Play (for alternate streams without stream data)
 * - Racing TV Extra channels (for additional active streams)
 *
 * Each channel includes metadata like title, programme information, imagery, and streaming data.
 *
 * @async
 * @function getLiveChannelsInfo
 * @returns {Promise<Channel[]>} A promise that resolves to an array of formatted `Channel` objects representing the live channels.
 *
 * @example
 * const channels = await getLiveChannelsInfo();
 * console.log(channels[0].name); // "Racing TV"
 */
export const getLiveChannelsInfo = async (): Promise<Channel[]> => {
  const videos = (await fetchGet("videos/streams")) as {
    schedule_slots: ScheduleSlot[];
    streams: Stream[];
  };
  const { schedule_slots, streams: allStreams } = videos;

  const channels: Channel[] = [];

  const activeStreams: Stream[] = allStreams.filter(
    (s) => s.active.state === "on_until"
  );

  activeStreams?.forEach((stream, index) => {
    const { meeting } = stream;
    // RUK 24/7 event
    if (index == 0) {
      channels.push({
        name: "Racing TV",
        title: "Main live channel",
        id: undefined,
        sourceEventType: undefined,
        programmeTitle: schedule_slots[0]?.episode.programme.title,
        programmeDescription: schedule_slots[0]?.episode.epg_synopsis,
        backgroundImage:
          stream.preview_image_url || allStreams[0]?.placeholder_image_url,
        streamData: stream.stream_data,
        free: stream.free,
        createdAt: stream.created_at,
        duration: stream.duration,
        startedAt: stream.started_at,
        endedAt: stream.ended_at,
        startTime: new Date(stream?.started_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
        endTime: new Date(stream?.ended_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
      });
      // if there is no stream data and the source event type is alternate
      // then it is Racing TV Play
    } else if (
      !stream.stream_data &&
      stream.source_event_type === "alternate"
    ) {
      channels.push({
        name: `Racing TV Play`,
        title: stream.title,
        id: stream.id,
        sourceEventType: stream.source_event_type,
        programmeTitle: stream.title,
        programmeDescription:
          "Racing TV Play – brilliant action from Racing TV 24 hours a day",
        backgroundImage:
          stream.preview_image_url || stream.placeholder_image_url,
        streamData: stream.stream_data,
        free: stream.free,
        createdAt: stream.created_at,
        duration: stream.duration,
        startedAt: stream.started_at,
        endedAt: stream.ended_at,
        startTime: new Date(stream?.started_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
        endTime: new Date(stream?.ended_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
      });
    }
    // other active streams
    else {
      channels.push({
        name: `Racing TV extra ${index}`,
        title: meeting?.track?.name
          ? `${meeting?.track?.name} live`
          : stream.title,
        id: stream.id,
        sourceEventType: stream.source_event_type,
        slug: meeting?.track?.slug,
        programmeTitle: meeting?.track?.name
          ? `${meeting?.track?.name}`
          : stream.title,
        programmeDescription:
          "Watch live racing action from " + meeting?.track?.name, // No description for extra streams - https://www.pivotaltracker.com/story/show/185774281
        backgroundImage:
          stream.preview_image_url || stream.placeholder_image_url,
        streamData: stream.stream_data,
        createdAt: stream.created_at,
        duration: stream.duration,
        startedAt: stream.started_at,
        endedAt: stream.ended_at,
        startTime: new Date(stream.started_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
        endTime: new Date(stream.ended_at).toLocaleTimeString(
          "en-GB",
          timeOptions
        ),
      });
    }
  });

  return channels;
};

export const checkFreeVideoPeriod = async (): Promise<APIResponse<Player>> => {
  return await fetchGet(`member/watch/streams/channel`);
};

export const getMemberPlans = async (): Promise<APIResponse<MemberPlans>> => {
  return await fetchGet(`member/plans`);
};
